import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AudioPlayer = ({ currentTrack, isPlaying, onPlayPause, onNext, onPrevious }) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(347); // 5:47 in seconds
  const [volume, setVolume] = useState(75);
  const [isMuted, setIsMuted] = useState(false);
  const [isShuffled, setIsShuffled] = useState(false);
  const [repeatMode, setRepeatMode] = useState('off'); // off, one, all

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= duration) {
            onNext();
            return 0;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, duration, onNext]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimelineClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    setCurrentTime(Math.floor(newTime));
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const toggleShuffle = () => {
    setIsShuffled(!isShuffled);
  };

  const cycleRepeat = () => {
    const modes = ['off', 'one', 'all'];
    const currentIndex = modes.indexOf(repeatMode);
    setRepeatMode(modes[(currentIndex + 1) % modes.length]);
  };

  const getRepeatIcon = () => {
    switch (repeatMode) {
      case 'one':
        return 'Repeat1';
      case 'all':
        return 'Repeat';
      default:
        return 'Repeat';
    }
  };

  const progress = (currentTime / duration) * 100;

  return (
    <div className="w-full glass bg-surface/95 backdrop-blur-cosmic rounded-xl p-6 border border-border">
      {/* Track Info */}
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center animate-gentle-pulse">
          <Icon name="Music" size={24} color="white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-heading font-semibold text-text-primary truncate">
            {currentTrack}
          </h3>
          <p className="text-sm text-text-secondary font-mono">
            Planetary Composition • Generated from your birth chart
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            iconName="Heart"
            onClick={() => console.log('Add to favorites')}
            className="w-10 h-10 p-0"
          />
          <Button
            variant="ghost"
            iconName="Share"
            onClick={() => console.log('Share track')}
            className="w-10 h-10 p-0"
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-text-secondary font-mono mb-2">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
        <div 
          className="relative h-2 bg-surface rounded-full cursor-pointer group"
          onClick={handleTimelineClick}
        >
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
          <div 
            className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-primary rounded-full shadow-elevation-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            style={{ left: `calc(${progress}% - 8px)` }}
          />
        </div>
      </div>

      {/* Main Controls */}
      <div className="flex items-center justify-center space-x-4 mb-6">
        <Button
          variant="ghost"
          iconName="Shuffle"
          onClick={toggleShuffle}
          className={`w-10 h-10 p-0 ${isShuffled ? 'text-accent' : 'text-text-secondary'}`}
        />
        <Button
          variant="ghost"
          iconName="SkipBack"
          onClick={onPrevious}
          className="w-12 h-12 p-0"
        />
        <Button
          variant="primary"
          iconName={isPlaying ? "Pause" : "Play"}
          onClick={onPlayPause}
          className="w-16 h-16 p-0 rounded-full shadow-elevation-3 hover:shadow-elevation-4"
        />
        <Button
          variant="ghost"
          iconName="SkipForward"
          onClick={onNext}
          className="w-12 h-12 p-0"
        />
        <Button
          variant="ghost"
          iconName={getRepeatIcon()}
          onClick={cycleRepeat}
          className={`w-10 h-10 p-0 ${repeatMode !== 'off' ? 'text-accent' : 'text-text-secondary'}`}
        />
      </div>

      {/* Volume Control */}
      <div className="flex items-center space-x-3">
        <Button
          variant="ghost"
          iconName={isMuted || volume === 0 ? "VolumeX" : volume < 50 ? "Volume1" : "Volume2"}
          onClick={toggleMute}
          className="w-8 h-8 p-0 text-text-secondary"
        />
        <div className="flex-1 relative">
          <input
            type="range"
            min="0"
            max="100"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-full h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
          />
        </div>
        <span className="text-xs text-text-secondary font-mono w-8 text-right">
          {isMuted ? 0 : volume}
        </span>
      </div>

      {/* Waveform Visualization */}
      <div className="mt-6 flex items-center justify-center space-x-1 h-12">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className={`w-1 bg-gradient-to-t from-primary to-secondary rounded-full transition-all duration-200 ${
              isPlaying ? 'animate-gentle-pulse' : ''
            }`}
            style={{
              height: `${Math.random() * 100 + 20}%`,
              animationDelay: `${i * 50}ms`
            }}
          />
        ))}
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
        }
        .slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default AudioPlayer;